// ─── PerformanceConfigCard.tsx ────────────────────────────────────────────────
import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/useLocale";
import { CalendarDays } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Button } from "@/components/ui/button";

type ConfigStatus = "active" | "inactive" | "not_configured";

interface IPerformanceConfigCardProps {
  title: string;
  description: string;
  startDate?: string | null;
  endDate?: string | null;
  status: ConfigStatus;
  onEdit?: () => void;
  onConfigure?: () => void;
}

export const PerformanceConfigCard = ({
  title,
  description,
  startDate,
  endDate,
  status,
  onEdit,
  onConfigure,
}: IPerformanceConfigCardProps) => {
  const { t } = useTranslation(["common", "company"]);
  const { formatDate } = useLocale();
  const statusLabels: Record<ConfigStatus, string> = {
    active: t("company:performance.config_status_active"),
    inactive: t("company:performance.config_status_inactive"),
    not_configured: t("company:performance.config_status_not_configured"),
  };
  const label = statusLabels[status];
  const isConfigured = status !== "not_configured";

  return (
    <div className="flex flex-col justify-between bg-white border  rounded-xl p-4  gap-4">
      {/* Top row */}
      <div className="flex items-start justify-between">
        <div className="bg-info-muted p-3 rounded-xs">
          <CalendarDays size={20} className="text-primary" />
        </div>
        <Badge variant={statusToVariant(status)} dot={false}>
          {label}
        </Badge>
      </div>

      {/* Title & description */}
      <div>
        <h3 className="text-md font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
      </div>

      {/* Dates */}
      <div className="flex items-center gap-12 text-xs mb-6">
        <div>
          <p className="text-gray-400 uppercase tracking-wide mb-0.5">
            {t("company:performance.start_date_label")}
          </p>
          <p className="font-semibold text-gray-800">
            {startDate ? formatDate(new Date(startDate), "dd MMMM, yyyy") : "-"}
          </p>
        </div>
        <div className="w-px h-8 bg-border" />
        <div>
          <p className="text-gray-400 uppercase tracking-wide mb-0.5">
            {t("company:performance.end_date_label")}
          </p>
          <p className="font-semibold text-gray-800">
            {endDate ? formatDate(new Date(endDate), "dd MMMM, yyyy") : "-"}
          </p>
        </div>
      </div>

      {/* CTA */}
      {isConfigured ? (
        <Button variant="primary" className="text-sm! w-full" onClick={onEdit}>
          {t("company:performance.edit_settings")}
        </Button>
      ) : (
        <Button
          variant="outline"
          className="w-full text-sm! text-primary border-primary"
          onClick={onConfigure}
        >
          {t("company:performance.configure")}
        </Button>
      )}
    </div>
  );
};
